<template>
  <div v-if="!isSkipped">
    <SurveyForm
      v-if="(!isSubmitted && showOnlySurvey) || showSurveyInReadOnly" 
      :schema="surveySchema"
      :submittedValues="submittedValues"
      :isSurveySubmitted="showSurveyInReadOnly"
      :surveyId="surveyId" 
      :isFirstResponse="isFirstResponse" 
      :triggerType="triggerType"
      @survey-submit="onSurveySubmit" 
      @survey-skipped="onSurveySkipped" 
      @survey-closed="onSurveyClosed"
    />
    <SubmittedForm
      v-else-if="isSubmitted && !showOnlySurvey" 
      :submittedData="eventPayload" 
      :surveyId="surveyId"
      :schema="surveySchema"
      @show-survey-submitted="showSubmittedSurvey"
    />
  </div>
</template>
<script>
import SurveyForm from './components/SurveyForm.vue'
import SubmittedForm from './components/SubmittedForm.vue'
import { cloneDeep } from 'lodash'
import { chatEvents, surveyForm, feedback } from '@/services/constants'
import { emitEventToParentWindow, getEventDataPoints } from '../../../../../services/utils';
import { eventType } from '../../../../../services/constants';

export default {
  name: 'SurveyView',
  components: {
    SurveyForm,
    SubmittedForm
  },
  props: {
    message: Object,
    isFirstResponse: {
        type: Boolean,
        default: false
    },
    showOnlySurvey: {
      type: Boolean,
      default: false
    },
    submittedValues: {
      type: Object,
      default: () => {}
    },
    isSurveySubmitted: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      surveySchema: {},
      surveyId: '',
      isSubmitted: false,
      isSkipped: false,
      eventPayload: {},
      triggerType: '',
      conversationId: '',
      showSurveyInReadOnly: false
    }
  },
  computed: {
    isSurveyTypePersistent() {
      return this.surveySchema.isPersistentSurveyEnabled
    },
    isSurveyTypeAgentFeedback() {
      return this.triggerType === surveyForm.AGENTDESK_FEEDBACK_REVIEW
    },
  },
  watch: {
    message() {
      this.populateLocalState()
    }
  },
  created () {
    this.populateLocalState()
    this.showSurveyInReadOnly = this.isSurveySubmitted
  },
  methods: {
    focusOnAppropriateElement() {
      const textarea = document.querySelector('.custom-textarea'); 
      textarea && textarea.focus()
    },
    populateLocalState () {
      if (!this.message?.messageValue) return
      const currMsg = cloneDeep(this.message)
      const schema = JSON.parse(currMsg.messageValue?.schema)
      const status = currMsg.messageValue?.status[0]
      // if survey form is already submitted
      if (status === surveyForm.SUBMITTED) {
        this.isSubmitted = 'submitSurveyInfo' in schema
        this.isSkipped = !this.isSubmitted
      }
      this.eventPayload = schema.submitSurveyInfo || {}
      this.surveySchema = schema?.payload || {}
      if (this.isSubmitted) this.updateEventPayload()
      this.surveyId = schema?.surveyId || ''
      this.triggerType = schema?.triggerType
      this.conversationId = currMsg.conversationId
      !this.isSubmitted && this.$store.dispatch('updateSurveyFormState', { isValid: true, data: this.message, isFirstResponse: this.isFirstResponse })
    },
    updateEventPayload () {
      const rating = this.eventPayload?.rating
      if (!rating) return
      const surveyRatingTypeEnabledInfo = this.surveySchema?.surveyRatingTypeEnabledInfo
      const sentinent = rating < surveyRatingTypeEnabledInfo.criteria
      const key = sentinent ? surveyForm.NEGATIVE_MAP_KEY : surveyForm.POSITIVE_MAP_KEY
      const suggestionTitle = this.surveySchema[key].title

      this.eventPayload.feedbackValue = sentinent ? surveyForm.NEGATIVE : surveyForm.POSITIVE
      this.eventPayload.suggestionTitle = suggestionTitle
    },
    async onSurveySkipped() {
      this.clearConversationIfRequired()
      this.isSkipped = true

      const formAlertTag = document.getElementById('form-state-alert')
      formAlertTag.setAttribute('aria-label', '')
      setTimeout(() => {
        formAlertTag.setAttribute('aria-label', 'Survey is skipped')
      }, 0)

      // update form status in chats array
      const data = cloneDeep(this.message)
      data.hide = true
      data.messageValue.status[0] = surveyForm.SUBMITTED
      this.$store.dispatch('updateFormStatus', data)
      await this.$store.dispatch('sendIoTEvent',{event_name: 'FORM_SUBMISSION', data})
      if (this.message?.requestId) {
        this.$store.dispatch('FormControlActions', { 
          conversationId: this.conversationId,
          hide: true,
          userMessage: {
            payload: (this.triggerType === `${surveyForm.WIDGET_EVENT_CHAT_END}` || this.triggerType === `${surveyForm.WIDGET_EVENT_IDLE_USER}`) ? `${surveyForm.SKIP_EVENT}${surveyForm.DISABLE_RESUME_WORKFLOW}^$^requestId::value=${this.message.requestId}` : `${surveyForm.SKIP_EVENT}${surveyForm.ENABLE_RESUME_WORKFLOW}^$^requestId::value=${this.message.requestId}`,
            label: surveyForm.SKIP
          }
        })
      }
      this.$store.dispatch('setIdleTimerForSurvey') // set idle timer for survey form triggering
      this.$store.dispatch('updateSurveyFormState', { isValid: false, data: {}, isFirstResponse: false })
      if(!this.isSurveyTypePersistent && !this.isSurveyTypeAgentFeedback)  {
        emitEventToParentWindow(eventType.SURVEY_SKIPPED , getEventDataPoints())
      }
      if (this.triggerType === surveyForm.WIDGET_EVENT_CHAT_END) {
        await this.$store.dispatch('endChatSurveyFulfilledBeforeTimeout')
      }
      this.focusOnAppropriateElement()
    },
    async onSurveySubmit(data) {
      this.clearConversationIfRequired()
      this.eventPayload = data
      this.isSubmitted = true
      let surveyInfo = cloneDeep(data)
      surveyInfo.triggerType = this.triggerType
      delete surveyInfo?.feedbackValue
      // update form status in chats array
      const formData = cloneDeep(this.message)
      formData.messageValue.status[0] = surveyForm.SUBMITTED
      formData.messageValue.submitSurveyInfo = surveyInfo
      this.$store.dispatch('updateFormStatus', formData)
      if ((this.isSurveyTypePersistent || this.isSurveyTypeAgentFeedback) && this.$store.getters.isCSATValid) {
        const eventInfo = {
          feedbackValue: data.feedbackValue,
          submitSurveyInfo: surveyInfo
        }
        this.$store.dispatch('sendCSATSubmissionEvent', eventInfo)
        const feedbackValue = data.feedbackValue?.toUpperCase()
        if (feedbackValue !== this.$store.state.csat.rating) {
          this.$store.dispatch('updateCSATIcons', feedbackValue === feedback.POSITIVE)
        }
        const additionalFeedback = surveyInfo?.additionalFeedback
        this.$store.dispatch('updateCSATState', { comment: additionalFeedback })
        await this.$store.dispatch('sendIoTEvent',{ event_name: 'CSAT_SUBMIT', data: { feedbackValue, submitSurveyInfo: surveyInfo, timestamp: Date.now() } })
      } else {
        await this.$store.dispatch('sendIoTEvent',{ event_name: 'FORM_SUBMISSION', data: formData })
        const payload = {
          conversationId: this.conversationId,
          eventData: {
            eventType: chatEvents.SURVEY,
            subType: chatEvents.SUBMIT,
            eventInfo: {
              surveyId: this.surveyId,
              feedbackValue: data.feedbackValue,
              requestId: this.message.requestId,
              submitSurveyInfo: surveyInfo
            }
          },
          eventName: chatEvents.FEEDBACK
        }
        this.$store.dispatch('sendEvent', payload)
      }
      this.$store.dispatch('setIdleTimerForSurvey') // set idle timer for survey form triggering
      this.$store.dispatch('sendSurveyFormData', data)
      this.$store.dispatch('updateSurveyFormState', { isValid: false, data: {}, isFirstResponse: false })
      if(!this.isSurveyTypePersistent && !this.isSurveyTypeAgentFeedback)  {
        emitEventToParentWindow(eventType.SURVEY_SUBMITTED , getEventDataPoints())
      }
      if (this.triggerType === surveyForm.WIDGET_EVENT_CHAT_END) {
        await this.$store.dispatch('endChatSurveyFulfilledBeforeTimeout')
      }
    },
    clearConversationIfRequired() {
    if (this.$store.state.clearConversationState.status === 'pending') {
        this.$store.state.isTriggerMinimizeValid && this.$store.dispatch('minimizeCW')
        this.$store.dispatch('updateTriggerMinimizeValidState', false)
        this.$store.dispatch('updateClearConversationToPending','')
        this.$store.dispatch('handleClearConversationStatus', this.$store.state.widgetConfig?.endChatClearConversation)
      }
    },
    showSubmittedSurvey(value) {
      if (value) {
        this.$store.dispatch('updateSurveyFormState', { 
          isValid: true,
          data: this.message,
          isFirstResponse: this.isFirstResponse,
          submittedValues: this.eventPayload,
          isSurveySubmitted: true
         })
      }
    },
    onSurveyClosed() {
      this.showSurveyInReadOnly = false
      this.$store.dispatch('updateSurveyFormState', { isValid: false, data: {}, isFirstResponse: false })
      this.focusOnAppropriateElement()
    }
  }
}
</script>
<style lang="scss" scoped>
  
</style>
